import React, { useState } from 'react'
import type { TFunction } from 'i18next'
import { AnalysisSectionHeader } from '../../AnalysisSectionHeader/AnalysisSectionHeader'
import { AnalysisTabButtonMenu } from '../../molecules/AnalysisTabButtonMenu/AnalysisTabButtonMenu'
import {
  AnalysisTabContentContainer,
  SectionAnalysisContentContainer,
} from '../../molecules/SectionAnalysisStyles/SectionAnalysisStyles'
import { Container } from './BuildingCharacteristics.styles'
import { OverviewTab } from './components/OverviewTab'
import { ConstructionTimelineTab } from './components/ConstructionTimelineTab'
import { NeighborhoodContextTab } from './components/NeighborhoodContextTab'
import type { NeighborhoodAnalysis } from '@popety-io/types'

// Default translations for when t function is not provided
const defaultTranslations: Record<string, string> = {
  'states.loading': 'Loading...',
  'states.error': 'Error',
  'states.noData': 'No data available',
  'tabs.overview': 'Overview',
  'tabs.timeline': 'Timeline',
  'tabs.neighborhood': 'Neighborhood',
  'header.title': 'Building Characteristics',
  'header.subtitle': 'Detailed information about this building',
}

// Create a fallback t function
const createFallbackT = (): TFunction => {
  const fallbackT = ((key: string, options?: any) => {
    if (options?.error) {
      return `Error: ${options.error}`
    }
    return defaultTranslations[key] || key
  }) as TFunction
  return fallbackT
}

export interface BuildingCharacteristicsProps {
  building: any
  loading?: boolean
  error?: string | null
  className?: string
  /** Translation function - if not provided, English defaults will be used */
  t?: TFunction
  onFetchNeighborhoodAnalysis?: (
    buildingId: string,
    radius: number
  ) => Promise<NeighborhoodAnalysis>
  /** Pre-fetched neighborhood analysis data (optional, skips initial fetch if provided) */
  initialNeighborhoodAnalysis?: NeighborhoodAnalysis | null
  /** Callback when neighborhood analysis data changes (e.g., after radius change) */
  onNeighborhoodAnalysisChange?: (analysis: NeighborhoodAnalysis) => void
}

export const BuildingCharacteristics: React.FC<BuildingCharacteristicsProps> = (
  props
) => {
  // Defensive destructuring with fallbacks
  const {
    building,
    loading,
    error,
    className,
    t: externalT,
    onFetchNeighborhoodAnalysis,
    initialNeighborhoodAnalysis,
    onNeighborhoodAnalysisChange,
  } = props || {}

  // Use provided t function ONLY if it's actually a function, otherwise create fallback
  // Extra safety: wrap in try-catch and verify it's callable
  let t: TFunction
  try {
    if (externalT && typeof externalT === 'function') {
      // Test if it's actually callable
      t = externalT
    } else {
      t = createFallbackT()
    }
  } catch {
    t = createFallbackT()
  }

  const [activeTab, setActiveTab] = useState('overview')

  if (loading) {
    return <div>{t('states.loading')}</div>
  }

  if (error) {
    return <div>{t('states.error', { error })}</div>
  }

  if (!building) {
    return <div>{t('states.noData')}</div>
  }

  const tabs = [
    {
      id: 'overview',
      label: t('tabs.overview'),
      icon: 'layout-dashboard',
    },
    {
      id: 'timeline',
      label: t('tabs.timeline'),
      icon: 'clock',
    },
    {
      id: 'neighborhood',
      label: t('tabs.neighborhood'),
      icon: 'map-pin',
    },
  ]

  return (
    <Container className={className}>
      <AnalysisSectionHeader
        title={t('header.title')}
        subtitle={t('header.subtitle')}
      />

      <AnalysisTabButtonMenu
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId)}
      />

      <AnalysisTabContentContainer>
        <SectionAnalysisContentContainer>
          {activeTab === 'overview' && (
            <OverviewTab building={building} t={t} />
          )}
          {activeTab === 'timeline' && (
            <ConstructionTimelineTab building={building} t={t} />
          )}
          {activeTab === 'neighborhood' && (
            <NeighborhoodContextTab
              building={building}
              t={t}
              onFetchNeighborhoodAnalysis={onFetchNeighborhoodAnalysis}
              initialNeighborhoodAnalysis={initialNeighborhoodAnalysis}
              onNeighborhoodAnalysisChange={onNeighborhoodAnalysisChange}
            />
          )}
        </SectionAnalysisContentContainer>
      </AnalysisTabContentContainer>
    </Container>
  )
}

export default BuildingCharacteristics
